import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import * as OBC from "@thatopen/components";
import * as OBF from "@thatopen/components-front";
import * as FRAGS from '@thatopen/fragments';
import { toast, twoDecimals, showGlobalLoader } from 'utils/helpers';

const HIGHLIGHTER_SELECTION_NAME = 'select';
const MEASUREMENTS_TEMPLATE = {
  count: 0,
  volume: 0,
  area: 0,
  len: 0,
};

export const useBimViewer = () => {
  const { t } = useTranslation();
  const [groupedData, setGroupedData] = useState([]);
  const [selectedElements, setSelectedElements] = useState(null);
  const [selectionMeasurements, setSelectionMeasurements] = useState({ ...MEASUREMENTS_TEMPLATE });
  const [selectionProperties, setSelectionProperties] = useState(null);

  const containerRef = useRef(null);
  const componentsRef = useRef(null);
  const worldRef = useRef(null);
  const fragmentRef = useRef(null);
  const serializerRef = useRef(null);
  const finderRef = useRef(null);
  const modelsRef = useRef([]);
  const highlighterRef = useRef(null);
  const hiderRef = useRef(null);
  const measureVolumeRef = useRef(null);

  // Refs for data processing
  const fragmentBytesRef = useRef(null); // Keep for single file backwards compatibility if needed, but mainly we use direct loading now

  const initViewer = async () => {
    if (!containerRef.current || componentsRef.current) return;

    const container = containerRef.current;

    componentsRef.current = new OBC.Components();
    const worlds = componentsRef.current.get(OBC.Worlds);
    worldRef.current = worlds.create();

    worldRef.current.scene = new OBC.SimpleScene(componentsRef.current);
    worldRef.current.renderer = new OBC.SimpleRenderer(componentsRef.current, container);
    worldRef.current.camera = new OBC.SimpleCamera(componentsRef.current);

    componentsRef.current.init();

    worldRef.current.camera.controls.setLookAt(12, 6, 8, 0, 0, -10);

    worldRef.current.scene.setup();
    worldRef.current.scene.three.background = null;

    const grids = componentsRef.current.get(OBC.Grids);
    grids.create(worldRef.current);

    serializerRef.current = new FRAGS.IfcImporter();
    serializerRef.current.wasm = { absolute: true, path: "/wasm/" };

    finderRef.current = componentsRef.current.get(OBC.ItemsFinder);

    // Setup element hider
    hiderRef.current = componentsRef.current.get(OBC.Hider);

    // Setup volume measurement
    measureVolumeRef.current = componentsRef.current.get(OBF.VolumeMeasurement);
    measureVolumeRef.current.world = worldRef.current;
    measureVolumeRef.current.enabled = true;

    // Setup highlighter
    highlighterRef.current = componentsRef.current.get(OBF.Highlighter);
    highlighterRef.current.setup({ world: worldRef.current });
    highlighterRef.current.zoomToSelection = true;

    highlighterRef.current.events.select.onHighlight.add((data) => {
      onModelElementSelection(data);
    });

    highlighterRef.current.events.select.onClear.add(() => {
      onModelElementSelection(null);
    });

    await setupWasm();
  };

  const resetViewer = () => {
    // Clear selection visuals first to prevent errors during disposal if elements are selected
    if (highlighterRef.current) {
      try {
        highlighterRef.current.clear(HIGHLIGHTER_SELECTION_NAME);
      } catch (e) { console.warn(e); }
    }

    if (componentsRef.current) {
      try {
        componentsRef.current.dispose();
      } catch (e) {
        console.warn("Error disposing components:", e);
      }
      componentsRef.current = null;
    }

    // Clear refs
    highlighterRef.current = null;
    measureVolumeRef.current = null;
    hiderRef.current = null;
    finderRef.current = null;

    modelsRef.current = [];
    fragmentRef.current = null;
    setGroupedData([]);
    setSelectedElements(null);
    setSelectionMeasurements({ ...MEASUREMENTS_TEMPLATE });
    setSelectionProperties(null);

    if (containerRef.current) {
      containerRef.current.innerHTML = '';
    }
  };

  const setupWasm = async () => {
    const workerUrl = "/wasm/worker.mjs";
    fragmentRef.current = componentsRef.current.get(OBC.FragmentsManager);
    fragmentRef.current.init(workerUrl);
    worldRef.current.camera.controls.addEventListener("rest", () => fragmentRef.current.core.update(true));
  };

  const loadIfc = async (ifcAsArrayBuffer, uid) => {
    showGlobalLoader(true);

    try {
      const ifcBytes = new Uint8Array(ifcAsArrayBuffer);

      const fragmentBytes = await serializerRef.current.process({
        bytes: ifcBytes,
        progressCallback: (progress, data) => console.log(progress, data),
      });

      const model = await fragmentRef.current.core.load(fragmentBytes, { modelId: uid || `model-${Date.now()}` });
      model.hasProperties = true;

      model.useCamera(worldRef.current.camera.three);
      worldRef.current.scene.three.add(model.object);

      modelsRef.current.push(model);

      await fragmentRef.current.core.update(true);

    } catch (err) {
      console.error(err);
      toast(t('Klaida paruošiant IFC failą'), 'error');
    }

    showGlobalLoader(false);
  };

  const removeModel = async (uid) => {
    const modelIndex = modelsRef.current.findIndex((m) => m.modelId === uid);
    if (modelIndex === -1) return;

    const model = modelsRef.current[modelIndex];

    // Remove from scene
    if (worldRef.current && worldRef.current.scene) {
      worldRef.current.scene.three.remove(model.object);
    }

    // Dispose resources
    if (fragmentRef.current) {
      // Attempt to properly dispose the group via manager if method exists
      if (typeof fragmentRef.current.disposeGroup === 'function') {
        fragmentRef.current.disposeGroup(model);
      } else {
        // Fallback: Manual cleanup
        model.object.traverse((child) => {
          if (child.isMesh) {
            if (child.geometry) child.geometry.dispose();
            if (Array.isArray(child.material)) {
              child.material.forEach(m => m.dispose());
            } else if (child.material) {
              child.material.dispose();
            }
          }
        });
        // Try removing from internal map if accessible
        if (fragmentRef.current.groups && fragmentRef.current.groups.delete) {
          fragmentRef.current.groups.delete(model.uuid);
        }
      }
    }

    // Remove from array
    modelsRef.current.splice(modelIndex, 1);

    // Refresh tree
    await applyClassifications();
  };

  const applyClassifications = async (searchQuery = '') => {
    if (modelsRef.current.length === 0) {
      setGroupedData([]);
      return;
    }

    const rows = [];
    const classifier = componentsRef.current.get(OBC.Classifier);
    const finder = componentsRef.current.get(OBC.ItemsFinder); // Use the ref directly if possible, or get it again

    // Clear previous classifications to avoid ghost data
    if (classifier.list && classifier.list.clear) {
      classifier.list.clear();
    }
    if (finder.list && finder.list.clear) {
      finder.list.clear();
    }

    await classifier.byCategory({ classificationName: 'categories' });
    await classifier.byIfcBuildingStorey({ classificationName: 'levels' });

    const classifications = [
      { system: 'categories', label: t('Kategorijos') },
      { system: 'levels', label: t('Aukštai') },
    ];

    for (const classification of classifications) {
      const { system, label } = classification;
      const groups = classifier.list.get(system).keys().toArray();
      if (!groups) continue;

      rows.push({
        data: { name: label, system },
        children: groups
          .filter((group) => searchQuery ? group.toLowerCase().includes(searchQuery) : true)
          .map((group) => {
            if (system === 'levels') {
              finderRef.current.create(group, [{
                relation: {
                  name: "ContainedInStructure", query: {
                    categories: [/BUILDINGSTOREY/],
                    attributes: { queries: [{ name: /Name/, value: new RegExp(`^${group}$`, 'i') }] },
                  }
                },
              }]);
            }
            return { data: { name: group, system } };
          }),
      });
    }

    const structureTree = {
      data: { name: t('Struktūra'), system: 'structure' },
      children: [],
    };

    for (const model of modelsRef.current) {
      try {
        const structure = await model.getSpatialStructure();
        const tree = await getModelTree(model, structure, searchQuery);
        if (tree) structureTree.children.push(tree);
      } catch (e) {
        console.warn("Could not get spatial structure for model", model.modelId, e);
      }
    }

    rows.push(structureTree);

    setGroupedData(rows);
  };

  const getModelTree = async (model, structure, searchQuery = '') => {
    const { localId, category, children } = structure;

    if (category && children) {
      const row = {
        data: {
          name: category,
          modelId: model.modelId,
          children: JSON.stringify(children.map((item) => item.localId)),
        },
      };

      const processedChildren = [];
      for (const child of children) {
        const childRow = await getModelTree(model, child, searchQuery);
        if (childRow) processedChildren.push(childRow);
      }

      if (processedChildren.length > 0) {
        row.children = processedChildren;
      }

      if (!searchQuery) return row;
      if (processedChildren.length > 0) return row;
      return null;
    }

    if (localId !== null) {
      const item = model.getItem(localId);
      const attrs = await item.getAttributes();
      if (!attrs) return null;
      const name = String(attrs.getValue("Name"));

      const row = {
        data: {
          name,
          modelId: model.modelId,
          localId,
        },
      };

      const processedChildren = [];
      for (const child of children ?? []) {
        const childRow = await getModelTree(model, child, searchQuery);
        if (childRow) processedChildren.push(childRow);
      }
      if (processedChildren.length > 0) {
        row.children = processedChildren;
      }

      return row;
    }

    return null;
  };

  const onModelElementSelection = async (fragmentIdMap = null) => {
    setSelectedElements(fragmentIdMap);

    let measures = { ...MEASUREMENTS_TEMPLATE };
    if (fragmentIdMap) {
      measures.count = twoDecimals(countElementsInFragmentIdMap(fragmentIdMap));
    }

    setSelectionMeasurements(measures);

    if (!fragmentIdMap) {
      setSelectionProperties(null);
      return;
    }

    const allProperties = {};

        console.log('DEBUG: modelsRef check', { 
            count: modelsRef.current.length, 
            firstModel: modelsRef.current[0],
            keys: modelsRef.current[0] ? Object.keys(modelsRef.current[0]) : [],
            itemsIsArray: modelsRef.current[0] && Array.isArray(modelsRef.current[0].items),
            keyFragmentsIsMap: modelsRef.current[0] && modelsRef.current[0].keyFragments instanceof Map
        });
    
        // Iterate over fragment IDs to find their corresponding models and fetch data
        for (const fragmentId of Object.keys(fragmentIdMap)) {
          let model = null;
    
          // Try getting via FragmentsManager.list if available
          if (fragmentRef.current?.list?.get) {
            const frag = fragmentRef.current.list.get(fragmentId);
            if (frag) model = frag.group;
          }
    
          // Fallback: search in loaded models
          if (!model) {
            // If only one model is loaded, assume it is the one (optimization/fallback)
            if (modelsRef.current.length === 1) {
                model = modelsRef.current[0];
            } else {
                for (const m of modelsRef.current) {
                // Check if model contains this fragment
                // A FragmentsGroup (model) has 'items' which is an array of Fragments
                if (Array.isArray(m.items)) {
                    // console.log('Checking model items:', m.items.map(f => f.id)); 
                    if (m.items.some(f => f.id === fragmentId)) {
                    model = m;
                    break;
                    }
                }
                
                // Try checking keyFragments if items failed or didn't exist
                if (!model && m.keyFragments instanceof Map) {
                        for (const val of m.keyFragments.values()) {
                            if (val === fragmentId) {
                                model = m;
                                break;
                            }
                        }
                }
                }
            }
          }
      console.log('Selection Debug:', { fragmentId, modelFound: !!model, modelId: model?.modelId });

      if (!model) continue;

      const expressIds = Array.from(fragmentIdMap[fragmentId]);
      console.log('Express IDs:', expressIds);

      const properties = await getItemPropertySets(model, expressIds);
      console.log('Raw Properties:', properties);

      const propertiesValueMap = formatItemPsets(properties, true);

      // Merge properties
      // For now, we just merge them flat. 
      // If different elements are selected, properties might overlap or overwrite.
      // This is existing behavior (last one wins or mixed).
      Object.assign(allProperties, propertiesValueMap);
    }
    console.log({ allProperties, fragmentIdMap })
    setSelectionProperties(allProperties);
  };

  const countElementsInFragmentIdMap = (fragmentIdMap) => {
    return Object.keys(fragmentIdMap).reduce(
      (childCount, fragment) => fragmentIdMap[fragment].size + childCount,
      0
    )
  };

  const getItemPropertySets = async (model, localIds = []) => {
    if (!localIds.length || !model) return null;
    try {
      const result = await model.getItemsData(localIds, {
        attributesDefault: false,
        attributes: ["Name", "NominalValue"],
        relations: {
          IsDefinedBy: { attributes: true, relations: true },
          DefinesOcurrence: { attributes: false, relations: false },
        },
      });
      console.log('getItemsData result:', result);
      const [data] = result;
      return data?.IsDefinedBy ?? [];
    } catch (e) {
      console.error('Error in getItemsData:', e);
      return [];
    }
  };

  const formatItemPsets = (rawPsets, flatMap = false) => {
    if (!rawPsets) return {};
    let result = {};

    for (const [_, pset] of rawPsets.entries()) {
      const { Name: psetName, HasProperties } = pset;

      if (!("value" in psetName && Array.isArray(HasProperties))) continue;

      const props = {};

      for (const [_, prop] of HasProperties.entries()) {
        const { Name, NominalValue } = prop;

        if (!("value" in Name && "value" in NominalValue)) continue;

        const name = Name.value;
        const nominalValue = NominalValue.value;

        if (!(name && nominalValue !== undefined)) continue;

        props[name] = nominalValue;
      }

      result[psetName.value] = props;
    }

    if (flatMap) {
      const flatResult = {};
      for (const psetName of Object.keys(result)) {
        const props = result[psetName];
        for (const propName of Object.keys(props)) {
          flatResult[propName] = props[propName];
        }
      }
      result = flatResult;
    }

    return result;
  };

  const findModelIdMapFromNode = async (node) => {
    let result = {};
    if (!node) return result;

    if (node?.data.hasOwnProperty('localId') || node?.data.hasOwnProperty('children')) {
      let localIds = [];
      if (node?.children?.length) {
        localIds = nodeTreeToList(node);
      } else {
        localIds = [parseInt(node.data.localId)];
      }

      // Node should ideally know which model it belongs to, otherwise we assume the first one?
      // In getModelTree, we added modelId to the node data.
      if (node.data.modelId) {
        result[node.data.modelId] = new Set(localIds);
      } else if (modelsRef.current.length > 0) {
        // Fallback: use first model if no ID (shouldn't happen with updated tree)
        result[modelsRef.current[0].modelId] = new Set(localIds);
      }

    } else if (node?.data.hasOwnProperty('system')) {
      const finderQuery = finderRef.current.list.get(node?.data?.name);
      if (!finderQuery) return result;
      result = await finderQuery.test();
    } else {
      console.warn('Unknown node type for model ID map extraction', { node });
    }

    return result;
  };

  const nodeTreeToList = (tree) => {
    const list = [];
    const walk = (node) => {
      if (!node) return;
      if (node?.children?.length) {
        for (const c of node.children) walk(c);
      } else {
        const localId = node?.data?.localId;
        if (localId) list.push(parseInt(localId));
      }
    }
    walk(tree);
    return list;
  };

  const onFilterItemClick = async (node) => {
    let modelIdMap = await findModelIdMapFromNode(node);
    await hiderRef.current.isolate(modelIdMap);
    if (highlighterRef.current) {
      highlighterRef.current.highlightByID(
        HIGHLIGHTER_SELECTION_NAME,
        modelIdMap,
        true,
        true
      );
    }
  };

  const handleSelectionClear = () => {
    if (highlighterRef.current) {
      highlighterRef.current.clear(HIGHLIGHTER_SELECTION_NAME);
    }
    if (hiderRef.current) {
      hiderRef.current.set(true);
    }
    setSelectionMeasurements({ ...MEASUREMENTS_TEMPLATE });
    setSelectedElements(null);
    setSelectionProperties(null);
  };

  return {
    containerRef,
    initViewer,
    resetViewer,
    loadIfc,
    removeModel,
    applyClassifications,
    groupedData,
    selectedElements,
    selectionMeasurements,
    selectionProperties,
    onFilterItemClick,
    handleSelectionClear,
  };
};